/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Button,
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid2,
  Tooltip,
} from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { format } from "date-fns";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ApiErrorsDto, ErrorLogRequest, PortalErrorsDto } from "../../../types/ErrorLog";
import {
  COMMONCONSTANTS,
  dataGridHeaderHeight,
  dataGridRowHeight,
  GLOBALSETTINGS,
  GRIDCOLUMNKEYS,
  SEARCHBOX,
} from "../../../data/constants";
import SearchBox from "../../../components/form/SearchBox/SearchBox";
import ErrorLogDetails from "./ErrorLogDetails";
import useApi from "../../../hooks/useApi";
import { getApiError, getPortalError } from "../../../services/errorService";
import { filterDataBySearch } from "../../../utils/commonHelper";
import { GLOBALMESSAGES } from "../../../constants/messages";
import { LoggerType } from "../../../data/Enums";
import { getCurrentESTDate } from "../../../utils/dateHelper";
import CustomLoadingOverlay from "../../../components/ui/CustomLoadingOverlay";
import { useResizableColumns } from "../../../hooks/useResizableColumns";
import { usePersistedColumnVisibility } from "../../../hooks/usePersistedColumnVisibility";

const ErrorLog: React.FC = () => {
  const [dateFrom, setDateFrom] = useState<Date | null>(getCurrentESTDate());
  const [dateTo, setDateTo] = useState<Date | null>(getCurrentESTDate());
  const { loading, executeApi } = useApi();
  const [searchResult, setSearchResult] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<PortalErrorsDto | ApiErrorsDto | null>(null);
  const [allData, setAllData] = useState<PortalErrorsDto[] | ApiErrorsDto[]>([]);
  const [pageState, setPageState] = useState<{
    data: PortalErrorsDto[] | ApiErrorsDto[] | null;
    total: number;
  }>({
    data: null,
    total: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [loggerType, setLoggerType] = useState<LoggerType>(LoggerType.FrontEnd);
  const [currentLoggerType, setCurrentLoggerType] = useState<LoggerType>(LoggerType.FrontEnd);

  const loadData = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    const errorLogRequest: ErrorLogRequest = {
      fromDate: format(dateFrom, GLOBALSETTINGS.DATE_FORMAT_ISO),
      toDate: format(dateTo, GLOBALSETTINGS.DATE_FORMAT_ISO),
      source: COMMONCONSTANTS.PORTALSOURCE,
    };

    let errorLogData = null;
    if (loggerType === LoggerType.FrontEnd) {
      errorLogData = await executeApi<ErrorLogRequest, PortalErrorsDto[]>(
        getPortalError,
        errorLogRequest
      );
    } else {
      errorLogData = await executeApi<ErrorLogRequest, ApiErrorsDto[]>(
        getApiError,
        errorLogRequest
      );
    }

    setAllData(errorLogData ?? []);
    setPageState({
      data: errorLogData,
      total: errorLogData && errorLogData?.length > 0 ? errorLogData?.length : 0,
    });
    setCurrentLoggerType(loggerType); // Update the current logger type after data is loaded
  }, [dateFrom, dateTo, loggerType, executeApi]);

  const handleView = (row: PortalErrorsDto | ApiErrorsDto) => {
    setSelectedRow(row);
    setOpen(true);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoClick = async () => {
    if (!dateFrom || !dateTo) {
      setError(GLOBALMESSAGES.BOTH_DATES);
      return;
    }

    if (dateTo < dateFrom) {
      setError(GLOBALMESSAGES.TO_DATE_GREATER);
      return;
    }

    setError(null);
    await loadData();
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (searchResult) {
      const lowerText = searchResult.toLowerCase();
      const filtered =
        currentLoggerType === LoggerType.FrontEnd
          ? filterDataBySearch<PortalErrorsDto>(allData as PortalErrorsDto[], lowerText)
          : filterDataBySearch<ApiErrorsDto>(allData as ApiErrorsDto[], lowerText);

      setPageState({
        data: filtered ?? [],
        total: filtered?.length,
      });
    } else {
      setPageState({
        data: allData ?? [],
        total: allData?.length,
      });
    }
  }, [searchResult, allData, currentLoggerType]);

  const handleSearchText = useCallback((text: string) => {
    setSearchResult((prev) => (prev !== text ? text : prev));
  }, []);

  const baseColumns: GridColDef[] = [
    { field: "userId", headerName: "User ID", width: 70 },
    { field: "userName", headerName: "User", flex: 0.1, minWidth: 150 },
    {
      field: "errorDate",
      headerName: "Date Time",
      flex: 0.2,
      minWidth: 155,
      valueFormatter: (value) => (value ? format(value, GLOBALSETTINGS.DATE_FORMAT_TIME) : ""),
    },
    { field: "summary", headerName: "Summary", flex: 0.4, minWidth: 200 },
    { field: "ipAddress", headerName: "IP Address", width: 130 },    
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.1,
      minWidth: 80,
      hideable: false,
      filterable: false,
      sortable: false,
      disableColumnMenu: false,
      renderCell: (params) => (
        <GridActionsCellItem
          icon={
            <Tooltip title="View details" placement="top">
              <VisibilityIcon />
            </Tooltip>
          }
          label="View"
          onClick={() => handleView(params.row)}
        />
      ),
    },
  ];

  const apiColumns: GridColDef[] = [
    { field: "id", headerName: "ID", flex: 0.1, minWidth: 50 },
    { field: "message", headerName: "Message", flex: 0.4, minWidth: 300 },
    { field: "messageTemplate", headerName: "Message Template", flex: 0.3, minWidth: 200 },
    { field: "level", headerName: "Level", width: 70 },
    {
      field: "timeStamp",
      headerName: "Time Stamp",
      flex: 0.3,
      minWidth: 160,
      valueFormatter: (value) => (value ? format(value, GLOBALSETTINGS.DATE_FORMAT_TIME) : ""),
    },
    { field: "environmentName", headerName: "Environment Name", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.1,
      minWidth: 80,
      hideable: false,
      filterable: false,
      sortable: false,
      disableColumnMenu: false,
      renderCell: (params) => (
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="View"
          onClick={() => handleView(params.row)}
        />
      ),
    },
  ];

  const initialColumns = currentLoggerType === LoggerType.FrontEnd ? baseColumns : apiColumns;

  const { columnState, setColumnState, pinnedColumns, setPinnedColumns, handleColumnResize } =
    useResizableColumns(initialColumns);

  useEffect(() => {
    setColumnState(currentLoggerType === LoggerType.FrontEnd ? baseColumns : apiColumns);
  }, [currentLoggerType, setColumnState]);

  // The two logger types are different column sets in the same grid, so each keeps its own
  // saved "Manage columns" selection.
  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    currentLoggerType === LoggerType.FrontEnd
      ? GRIDCOLUMNKEYS.ERROR_LOG_FRONTEND
      : GRIDCOLUMNKEYS.ERROR_LOG_API
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <section className="mb-3">
        <Card>
          <CardContent>
            <Typography variant="h5" component="div" className="mb-3 card-title">
              Error Logs
            </Typography>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <Box width="100%" textAlign="left">
                <Typography variant="h5">Select Date Range</Typography>
              </Box>
              <Grid2 container spacing={2} width="100%" alignItems="center">
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <DatePicker
                    label="Date From"
                    value={dateFrom}
                    onChange={(newValue) => {
                      setDateFrom(newValue);
                      if (newValue && dateTo && newValue <= dateTo) {
                        setError(null);
                      }
                    }}
                    slotProps={{
                      textField: { size: "small", fullWidth: true, inputProps: { readOnly: true } },
                    }}
                    maxDate={new Date()}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <DatePicker
                    label="Date To"
                    value={dateTo}
                    onChange={(newValue) => {
                      setDateTo(newValue);
                      if (newValue && dateFrom && newValue >= dateFrom) {
                        setError(null);
                      }
                    }}
                    slotProps={{
                      textField: { size: "small", fullWidth: true, inputProps: { readOnly: true } },
                    }}
                    maxDate={new Date()}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="dropdown-label" sx={{ fontSize: "14px", top: "-6px" }}>
                      Logger Type
                    </InputLabel>
                    <Select
                      labelId="dropdown-label"
                      value={loggerType}
                      onChange={(e) => setLoggerType(e.target.value as LoggerType)}
                      label="Logger Type"
                      sx={{ fontSize: "14px" }}
                    >
                      {Object.values(LoggerType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6, md: 3 }} display="flex">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGoClick}
                    fullWidth
                    className="filter-btn"
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    Go
                  </Button>
                </Grid2>
              </Grid2>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: "5px",
                    alignItems: "flex-start",
                    textAlign: "left",
                    alignSelf: "flex-start",
                  }}
                >
                  {error}
                </Alert>
              )}
              <Box width="100%" className="search-box-wrap">
                <SearchBox
                  searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER}
                  onSearch={handleSearchText}
                />
              </Box>

              <Box width="100%">
                <div style={{ height: 500, width: "100%" }}>
                  <DataGridPro
                    rows={pageState.data ?? []}
                    rowHeight={dataGridRowHeight}
                    {...pageState.data}
                    columns={columnState}
                    slots={{
                      loadingOverlay: CustomLoadingOverlay,
                    }}
                    columnHeaderHeight={dataGridHeaderHeight}
                    loading={loading}
                    rowCount={pageState.total}
                    pageSizeOptions={[]}
                    getRowId={(row) =>
                      currentLoggerType === LoggerType.FrontEnd ? row.errorLogId : row.id
                    }
                    hideFooterPagination
                    localeText={{ noRowsLabel: GLOBALMESSAGES.NOROWSTEXT }}
                    pinnedColumns={pinnedColumns}
                    onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
                    onColumnWidthChange={(params) =>
                      handleColumnResize(params.width, params.colDef)
                    }
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                  />
                </div>
              </Box>
              <ErrorLogDetails
                open={open}
                handleClose={handleClose}
                selectedRow={selectedRow}
                loggerType={currentLoggerType}
              />
            </Box>
          </CardContent>
        </Card>
      </section>
    </LocalizationProvider>
  );
};

export default ErrorLog;
